export interface ClaimStatisticData {
    count?: number;
    amount?: number;
    changePercent: number;
    changeDirection: 'up' | 'down';
}

export interface ClaimsStatistics {
    denials: ClaimStatisticData;
    deniedClaims: ClaimStatisticData;
    successfulAppeals: ClaimStatisticData;
}

export interface ClaimsDashboardStatistics {
    openCases: number;
    claimsInProgress: number;
    dollarsAtRisk: number;
    appealsYearToDate: number;
}

export enum ClaimStatus {
    PAID = 'paid',
    DENIED = 'denied',
    PARTIALLY_PAID = 'partially_paid',
    PENDING = 'pending',
    PATIENT_RESPONSIBILITY = 'patient_responsibility',
    SECONDARY_DENIED = 'secondary_denied',
    TERTIARY_DENIED = 'tertiary_denied',
    MISSING_CLAIM = 'missing_claim',
}

export const CLAIM_STATUS_OPTIONS = [
    { value: ClaimStatus.PAID, label: 'Paid' },
    { value: ClaimStatus.DENIED, label: 'Denied' },
    { value: ClaimStatus.PARTIALLY_PAID, label: 'Partially Paid' },
    { value: ClaimStatus.PENDING, label: 'Pending' },
    { value: ClaimStatus.PATIENT_RESPONSIBILITY, label: 'Patient Responsibility' },
    { value: ClaimStatus.SECONDARY_DENIED, label: 'Secondary Denied' },
    { value: ClaimStatus.TERTIARY_DENIED, label: 'Tertiary Denied' },
];

export enum ClaimType {
    INSTITUTIONAL = 'I',
    PROFESSIONAL = 'P',
    DENTAL = 'D',
}

export const CLAIM_TYPE_OPTIONS = [
    { value: ClaimType.INSTITUTIONAL, label: 'Institutional' },
    { value: ClaimType.PROFESSIONAL, label: 'Professional' },
    { value: ClaimType.DENTAL, label: 'Dental' },
];

/** An 835 that may duplicate a manual payment: same payer tier and amount, not joined automatically (REV-2585). */
export interface PossibleDuplicatePayment {
    manualAttachmentId: string;
    remitAttachmentId: string;
}

export interface DataWarnings {
    mrn_mismatch?: boolean;
    subscriber_id_mismatch?: boolean;
    patient_id_mismatch?: boolean;
    /** Cleared server-side once either payment is joined or removed. */
    possible_duplicate_payments?: PossibleDuplicatePayment[];
}

export interface Claim {
    id: string;
    claimNumber: string;
    patientName: string;
    medicalRecordNumber?: string;
    dob?: string;
    dataWarnings?: DataWarnings;
    dateOfService: string;
    placeOfService?: string;
    dateSubmitted: string;
    payer: string;
    billedAmount: number;
    paidAmount: number;
    patientBalance: number;
    status: ClaimStatus;
    claimType?: ClaimType; // I=Institutional, P=Professional, D=Dental
    denialReason?: string;
    cptCode: string;
    provider: string;
    assignedUser: string;
    attachmentCount?: number; // Count of EDI attachments
    claimAvailable?: boolean; // True if claim has 837 CLAIM attachment (required for display)
    appealLetter?: boolean; // True if an appeal letter was generated for this claim (REV-2507)
    appealed?: boolean; // An appeal was recorded on the claim (REV-2484)
    actionTypes?: ActionType[]; // The recorded action types, for the Action type column (REV-2628)
    linked?: boolean; // Linked to its correct-and-resubmit counterpart
    paidInclResubmission?: number | string | null; // The original's paid plus its corrected claim's; null without a live link (REV-2634)
    disposition?: string; // The latest claim review's disposition; '' when never reviewed (REV-2625)
    payers?: ClaimPayer[]; // Claim detail only: the payer levels the claim has (REV-2621)
    createdAt?: string; // ISO datetime when claim was first imported
    updatedAt?: string; // ISO datetime when claim was last modified
    diagnosisCode?: string;
    procedureCode?: string;
}

/** Minimal stand-in for a claim identified only by its number, shown while the
 * real claim data is being fetched (e.g. opening a claim preview by claim
 * number before its details have loaded). */
export const createPlaceholderClaim = (claimNumber: string): Claim => ({
    id: "",
    claimNumber,
    patientName: "",
    dateOfService: "",
    dateSubmitted: "",
    payer: "",
    billedAmount: 0,
    paidAmount: 0,
    status: ClaimStatus.PENDING,
    cptCode: "",
    provider: "",
    assignedUser: "",
    patientBalance: 0,
});

/** Operator for multi-value filters: all (and), any (or), or exclude matching values (isnot) */
export type FilterOperator = 'and' | 'or' | 'isnot';

/** Single filter entry sent to backend: operator + values array */
export interface ColumnFilterEntry<T = string[] | ClaimStatus[] | ClaimType[]> {
    operator: FilterOperator;
    values: T;
}

export interface AllFilters {
    status?: ColumnFilterEntry<ClaimStatus[]>;
    claimType?: ColumnFilterEntry<ClaimType[]>;
    payer?: ColumnFilterEntry<string[]>;
    provider?: ColumnFilterEntry<string[]>;
    denialReason?: ColumnFilterEntry<string[]>;
    assignedUser?: ColumnFilterEntry<string[]>;
    patient?: ColumnFilterEntry<string[]>;
    patientName?: ColumnFilterEntry<string[]>;
    medicalRecordNumber?: ColumnFilterEntry<string[]>;
    dateOfService?: string[];
    dob?: string[];
    // Plain date range, like dateOfService (REV-2507).
    dateSubmitted?: string[];
    // 'Yes' / 'No' -- see ClaimsStore.getUniqueColumnValues (REV-2507).
    appealLetter?: ColumnFilterEntry<string[]>;
    appealed?: ColumnFilterEntry<string[]>;
    // ACTION_TYPE_FILTER_OPTIONS plus 'None' (REV-2628)
    actionType?: ColumnFilterEntry<string[]>;
    // 'Linked' / 'Not linked': linked to its correct-and-resubmit counterpart
    linked?: ColumnFilterEntry<string[]>;
    // [min, max], either end may be '' (REV-2634)
    paidInclResubmission?: ColumnFilterEntry<string[]>;
    // CLAIM_DISPOSITION_OPTIONS plus 'None' (REV-2625)
    disposition?: ColumnFilterEntry<string[]>;
    placeOfService?: ColumnFilterEntry<string[]>;
    dataWarnings?: ColumnFilterEntry<string[]>;
    patientBalance?: ColumnFilterEntry<string[]>;
    cptCode?: ColumnFilterEntry<string[]>;
    procedureCode?: ColumnFilterEntry<string[]>;
}

// EDI Upload types
export interface ClaimsUploadResult {
    files_processed: number;
    claims_created: number;
    claims_updated: number;
    duplicates: Array<{
        file: string;
        message: string;
    }>;
    errors: Array<{
        file: string;
        error: string;
    }>;
    invalid_pcns: Array<{
        file: string;
        pcn: string;
        reason: string;
        object_type: string;
    }>;
    claims: Claim[];
    claim_ids: string[]; // IDs of affected claims for targeted refresh
}

export interface EdiObject {
    objectType: 'CLAIM' | 'PAYMENT' | 'CLAIM_STATUS';
    id?: string;
    attachmentId?: string;
    /** "manual" for a payment an admin recorded from a payer letter (REV-2582). */
    source?: 'edi' | 'manual';
    /** Only on manual rows. */
    manual?: ManualPaymentInfo;
    /** Only on an 835 a manual payment was joined to (REV-2585). */
    matchedManual?: MatchedManualPayment;
    /** On an original claim: a remit of its corrected claim, which the original's
     * fee counts, embedded read-only in its detail (REV-2657). */
    fromClaimNumber?: string;
    patientControlNumber?: string;
    transaction?: {
        fileInfo?: {
            fileType: string;
            lastModifiedDateTime: string;
            name: string;
            url: string;
        }
    }
    [key: string]: unknown;
}

export interface ClaimLogEntry {
    id?: string;
    timestamp: string;
    message: string;
    status: string;
    reason: string;
    /** Who recorded the entry; null for system entries. */
    user?: string | null;
}

/** One appeal letter per chat on the claim (REV-2536). */
export interface ClaimAppealLetter {
    chatId: string;
    type: string;
    title: string;
    /** When the first version was generated. */
    generatedAt: string;
    /** Chat owner; null when unknown. */
    generatedBy: string | null;
    generatedByEmail: string | null;
    versionCount: number;
    /** False when the viewer can't see the letter's chat (another user's private chat). */
    available: boolean;
}

export type PayerTier = 'primary' | 'secondary' | 'tertiary';

/** A payer level the claim has, from its 837 and 835s (REV-2621). The name is a display hint. */
export interface ClaimPayer {
    tier: PayerTier;
    name: string | null;
}

/** What an admin records on a claim. `correct_resubmit` is always the admin's
 * choice, never taken from a letter, and a claim holds at most one (REV-2628). */
export type ActionType = 'appeal' | 'dofr' | 'correct_resubmit';

/** A PDF attached to the claim (REV-2538). `url` is server-rooted (/api/v1/...). */
export interface ClaimDocumentRef {
    id: string;
    name: string;
    contentType: string;
    size: number;
    url: string;
}

/** An action (appeal, DOFR appeal, correct and resubmit) an admin recorded on
 * the claim (REV-2538, REV-2628). Add-only. */
export interface ClaimAppeal {
    id: string;
    /** Calendar date, YYYY-MM-DD. */
    actionOn: string;
    /** Blank for a correct and resubmit. */
    payerTier: PayerTier | '';
    actionType: ActionType;
    comment: string;
    /** Who recorded it; null when unknown. */
    createdBy: string | null;
    createdAt: string;
    document: ClaimDocumentRef | null;
    /** On a correct and resubmit: its active link to the corrected claim (REV-2629). */
    link?: ClaimLink | null;
}

/** This claim's side of a link: the original holds the correct and resubmit
 * action, the corrected claim is the one the payer processed as new (REV-2629). */
export type ClaimLinkRole = 'original' | 'corrected';

/** A correct and resubmit action's link to the corrected (child) claim
 * (REV-2629). Keyed by claim number; unlinking keeps the row, stamped. */
export interface ClaimLink {
    id: string;
    parentClaimNumber: string;
    childClaimNumber: string;
    source: 'manual' | 'auto';
    /** Who linked it; null for an automatic link or when unknown. */
    linkedBy: string | null;
    linkedAt: string;
    unlinkedBy?: string | null;
    unlinkedAt?: string;
    unlinkReason?: string;
}

/** On the corrected claim: its link, with the original's action date. */
export interface ClaimResubmissionOf extends ClaimLink {
    /** Calendar date, YYYY-MM-DD. */
    actionOn: string;
}

/** Why a candidate was ranked where it was; the first three rank, the rest break ties. */
export type ClaimLinkMatchReason =
    | 'replacement'
    | 'sameDateOfService'
    | 'submittedAfterAction'
    | 'samePayer'
    | 'sameBilledAmount'
    | 'sameCptCodes';

/** A claim the link picker offers: the other side of the link. */
export interface ClaimLinkCandidate {
    claimId: string;
    claimNumber: string;
    patientName: string | null;
    dob: string | null;
    medicalRecordNumber: string | null;
    dateOfService: string | null;
    dateSubmitted: string | null;
    payer: string | null;
    billedAmount: number | string | null;
    cptCode: string | null;
    status: ClaimStatus | string | null;
    /** The original's correct and resubmit action date. */
    actionOn: string | null;
    matchReasons: ClaimLinkMatchReason[];
    /** Not the same MRN (or name and date of birth): the link has to be confirmed. */
    differentPatient: boolean;
}

export interface ClaimLinkCandidatesResponse {
    role: ClaimLinkRole;
    /** Best fit first, at most 50. */
    candidates: ClaimLinkCandidate[];
    /** The candidate to preselect; null when nothing points to one, or for a typed number. */
    bestFit: string | null;
    /** "Same date of service, submitted 12 days after the action". */
    bestFitReason: string | null;
}

export interface ClaimLinkCandidatesQuery {
    role: ClaimLinkRole;
    /** Only the same date of service as this claim (the API's default). */
    sameDateOfService: boolean;
    /** A typed number: overrides the filters, and the API refuses one it can't link. */
    claimNumber?: string;
}

export interface LinkClaimInput {
    otherClaimNumber: string;
    role: ClaimLinkRole;
    /** Required when the other claim is for a different patient. */
    confirmDifferentPatient: boolean;
}

/** A remit of the corrected claim dated on or before the action (REV-2657). */
export interface ClaimLinkEarlyRemit {
    /** Calendar date, YYYY-MM-DD. */
    paidOn: string;
    /** A decimal string, like the FeeChange values. */
    amount: string | null;
}

/** What linking would do, writing nothing (REV-2657). */
export interface ClaimLinkPreview {
    parentClaimNumber: string;
    childClaimNumber: string;
    /** The original's correct and resubmit date. */
    actionOn: string;
    differentPatient: boolean;
    /** The original's fee before and after; null when it's already invoiced. */
    feeChange: FeeChange | null;
    /** The corrected claim's remits dated on or before the action: the action
     * date or the picked claim is likely wrong. */
    remitsOnOrBeforeAction: ClaimLinkEarlyRemit[];
}

/** One FeeChange field: string decimals ("62.50"), ISO dates or plain strings. */
export interface FeeChangeValue<T = string | null> {
    before: T;
    after: T;
}

/** The claim and its success fee before and after an appeal (REV-2542) or a manual payment change (REV-2584). */
export interface FeeChange {
    changed: boolean;
    billingAppealDate: FeeChangeValue;
    basis: FeeChangeValue<'appeal' | 'letter' | null>;
    /** A percentage, e.g. "25.00". */
    recoveryPct: FeeChangeValue;
    paidAtAppeal: FeeChangeValue;
    paidAmount: FeeChangeValue;
    claimStatus: FeeChangeValue;
    successFee: FeeChangeValue;
    /** Past months (YYYY-MM) whose booked success fee changes. */
    restatedMonths: string[];
    /**
     * Set when the claim's remits can't say what was paid by the appeal date,
     * so the fee keeps the stored snapshot. The appeal can still be saved.
     */
    baselineFallback: FeeBaselineFallback | null;
}

export type FeeBaselineFallback = 'payment_without_date' | 'paid_amount_mismatch';

export interface RecordAppealInput {
    actionOn: string;
    /** Not sent for a correct and resubmit. */
    payerTier: PayerTier;
    /** An appeal / DOFR choice is ignored by the API when the claim has an appeal letter. */
    actionType: ActionType;
    comment: string;
    /** The admin's IANA zone, for the date rules. */
    timezone: string;
}

export interface RecordAppealResponse {
    appeal: ClaimAppeal;
    fee: FeeChange;
}

/** Who recorded a manual payment and its payer correspondence (REV-2582/2593). */
export interface ManualPaymentInfo {
    /** null when unknown. */
    createdBy: string | null;
    createdAt: string;
    document: ClaimDocumentRef | null;
}

/** The manual payment joined to an 835 (REV-2585), which no longer counts on its own. */
export interface MatchedManualPayment extends ManualPaymentInfo {
    /** The admin who joined it; null when it was joined automatically on 835 ingest (or unknown). */
    joinedBy: string | null;
}

export type ManualPaymentOutcome = 'paid' | 'denied';

/** A manual payment as the admin enters it; amounts are decimal strings ("250.00"). */
export interface ManualPaymentFields {
    outcome: ManualPaymentOutcome;
    payerTier: PayerTier;
    amount: string;
    /** Calendar date, YYYY-MM-DD. */
    paymentDate: string;
    payerName: string;
    checkOrEftNumber: string;
    /** The payer's claim number (ICN). */
    payerClaimNumber: string;
    patientResponsibility: string;
}

export interface RecordManualPaymentInput extends ManualPaymentFields {
    /** The admin's IANA zone, for the date rules. */
    timezone: string;
}

/** `fee` stays null until manual payment billing ships (REV-2584). */
export interface RecordManualPaymentResponse {
    payment: EdiObject;
    fee: FeeChange | null;
}

export interface RemoveManualPaymentResponse {
    fee: FeeChange | null;
}

/** `payment` is the remit the manual payment is joined to; `fee` stays null until REV-2584. */
export interface JoinManualPaymentResponse {
    payment: EdiObject;
    fee: FeeChange | null;
}

// Claim detail types
export interface ClaimDetailResponse {
    claim: Claim;
    ediObjects: EdiObject[];
    appealLetters?: ClaimAppealLetter[];
    appeals?: ClaimAppeal[];
    /** On a corrected claim: the link to its original (REV-2629). */
    resubmissionOf?: ClaimResubmissionOf | null;
    logEntries: ClaimLogEntry[];
}

// Upload History Dashboard types
export interface DailyStat {
    date: string;
    total_files: number;
    total_claims_found: number;
    total_claims_processed: number;
    by_status: {
        PROCESSED: number;
        SKIPPED: number;
        ERROR: number;
        UPLOADED: number;
        PROCESSING: number;
    };
    by_edi_type: {
        CLAIM: number;
        PAYMENT: number;
        CLAIM_STATUS: number;
    };
}

export interface DailyStatsResponse {
    daily_stats: DailyStat[];
    summary: {
        total_files: number;
        total_claims_found: number;
        total_claims_processed: number;
        success_rate: number;
        date_range: {
            start: string;
            end: string;
        };
    };
}

export interface FileHistory {
    id: string;
    file_name: string;
    file_hash: string;
    status: string;
    edi_object_type: string;
    claims_found: number;
    claims_processed: number;
    uploaded_at: string;
    date_uploaded: string;
    uploaded_by: string;
    date_submitted: string | null;
}

export interface FileHistoryDetail extends FileHistory {
    details: Array<{
        time: string;
        msg: string;
        type: string;
        claim_number?: string;
    }>;
}

export interface FileListResponse {
    files: FileHistory[];
    pagination: {
        page: number;
        page_size: number;
        total: number;
        total_pages: number;
    };
}

export interface ReprocessFileResult {
    file_id: string;
    file_name?: string;
    old_status?: string;
    new_status?: string;
    claims?: number;
    ok: boolean;
    error?: string | null;
}

export interface ReprocessFilesResponse {
    reprocessed: number;
    recovered: number;
    results: ReprocessFileResult[];
}

export type PreDefaultQuestionType = {
    question: string;
    agent_name?: string;
    placeholder?: string;
    /** Snapshot of the claim number captured atomically when the question was armed,
     * so claim context survives the ClaimPreview panel closing before Send. */
    claimNumber?: string;
    /** Set when armed from the main "Ask Reve" button (not a quick action) — no
     * predefined question/agent, just a blank chat scoped to the claim. Drives
     * ClaimPredefaultQuestionPreview to show ClaimWelcomeContent instead of a
     * placeholder system message. */
    isWelcome?: boolean;
}

/** Stored with saved filter views (alongside column filters); not sent as claim query filters. */
export type SavedClaimViewFilters = AllFilters & {
    orderBy?: { field: string; direction: 'asc' | 'desc' } | null;
    columnVisibility?: Record<string, boolean>;
};

export interface ClaimFilterView {
    id: string;
    name: string;
    filters: SavedClaimViewFilters;
}

// Filter type per column; anything absent defaults to 'list'.
// Read by both ClaimsTable's popover and SaveViewModal's operator labels --
// keep it the only copy, or the two drift.
export const CLAIMS_COLUMN_FILTER_TYPE = {
    claimNumber: 'text' as const,
    payer: 'text' as const,
    patientName: 'text' as const,
    medicalRecordNumber: 'text' as const,
    placeOfService: 'text' as const,
    cptCode: 'text' as const,
    procedureCode: 'text' as const,
    diagnosisCode: 'text' as const,
    denialReason: 'text' as const,
    dateOfService: 'dateRange' as const,
    dob: 'dateRange' as const,
    billedAmount: 'priceRange' as const,
    paidAmount: 'priceRange' as const,
    paidInclResubmission: 'priceRange' as const,
    patientBalance: 'priceRange' as const,
    updatedAt: 'dateRange' as const,
    createdAt: 'dateRange' as const,
    dateSubmitted: 'dateRange' as const,
};

export type ClaimsColumnFilterKind =
    (typeof CLAIMS_COLUMN_FILTER_TYPE)[keyof typeof CLAIMS_COLUMN_FILTER_TYPE];


/** Claim review dispositions (llm-server Disposition); the filter adds 'None' for claims never reviewed. */
export const CLAIM_DISPOSITION_OPTIONS = ['No Action', 'Correct', 'Appeal', 'Correct & Appeal', 'Verify Payment'];
export const CLAIM_DISPOSITION_NONE = 'None';

/** Action type filter values (the record dialog's labels); the filter adds 'None' for claims without one. */
export const ACTION_TYPE_FILTER_OPTIONS = ['Appeal', 'DOFR Appeal', 'Correct and resubmit'];
export const ACTION_TYPE_FILTER_NONE = 'None';
export const LINKED_FILTER_OPTIONS = ['Linked', 'Not linked'];

/** Column ids and labels for the claims grid (visibility toggles and Save View summary). */
export const CLAIMS_TABLE_COLUMN_OPTIONS = [
    { id: 'claimNumber', label: 'Claim #', visible: true, required: true, mandatory: true },
    { id: 'patientName', label: 'Patient', visible: true, },
    { id: 'claimType', label: 'Type', visible: true, },
    { id: 'medicalRecordNumber', label: 'MRN', visible: true, },
    { id: 'dateOfService', label: 'DOS', visible: true, },
    { id: 'placeOfService', label: 'POS', visible: true, },
    { id: 'procedureCode', label: 'Procedure', visible: true, },
    { id: 'diagnosisCode', label: 'Diagnostic', visible: true, },
    { id: 'billedAmount', label: 'Billed', visible: true, },
    { id: 'paidAmount', label: 'Paid', visible: true, },
    { id: 'patientBalance', label: 'Balance', visible: true, },
    { id: 'status', label: 'Status', visible: true, },
    { id: 'assignedUser', label: 'Assignee', visible: true, },
    { id: 'payer', label: 'Payer', visible: false, },
    { id: 'dob', label: 'DOB', visible: false, },
    { id: 'denialReason', label: 'Denial Codes', visible: false, },
    { id: 'dateSubmitted', label: 'Submitted', visible: false, },
    { id: 'appealLetter', label: 'Appeal letter', visible: true, },
    { id: 'appealed', label: 'Appealed', visible: false, },
    { id: 'actionType', label: 'Action type', visible: false, },
    { id: 'linked', label: 'Linked', visible: false, },
    { id: 'paidInclResubmission', label: 'Paid incl. resubmission', visible: false, },
    { id: 'disposition', label: 'Disposition', visible: false, },
    { id: 'createdAt', label: 'Imported', visible: false, },
    { id: 'updatedAt', label: 'Last updated', visible: false, },
] as Array<{
    id: string;
    label: string;
    required?: boolean;
    visible?: boolean;
    mandatory?: boolean;
}>;

/** Column id derived from CLAIMS_TABLE_COLUMN_OPTIONS. */
export type ClaimsTableColumnId = (typeof CLAIMS_TABLE_COLUMN_OPTIONS)[number]['id'];

/** ClaimsTable's display order (before pinned columns move first), which differs
 * from the column menu's CLAIMS_TABLE_COLUMN_OPTIONS order. Shared by the table
 * and the CSV export so the file's columns read like the screen. */
export const CLAIMS_TABLE_COLUMN_ORDER: ClaimsTableColumnId[] = [
    'claimNumber',
    'claimType',
    'patientName',
    'medicalRecordNumber',
    'dob',
    'dateOfService',
    'placeOfService',
    'procedureCode',
    'diagnosisCode',
    'payer',
    'billedAmount',
    'paidAmount',
    'patientBalance',
    'status',
    'denialReason',
    'assignedUser',
    'dateSubmitted',
    'appealLetter',
    'appealed',
    'actionType',
    'linked',
    'paidInclResubmission',
    'disposition',
    'createdAt',
    'updatedAt',
];

/** Per-column user preference: visibility and pin (required) state. */
export interface ClaimsTableColumnSetting {
    id: ClaimsTableColumnId;
    visible: boolean;
    required: boolean;
}

/** Column ids and labels for the upload history grid (visibility + pin toggles). */
export const UPLOAD_HISTORY_TABLE_COLUMN_OPTIONS = [
    { id: 'uploaded_at', label: 'Date', required: true, mandatory: true },
    { id: 'status', label: 'Status' },
    { id: 'edi_object_type', label: 'EDI Type' },
    { id: 'claims_found', label: 'Claims#' },
    { id: 'claims_processed', label: 'Processed' },
    { id: 'file_name', label: 'File Name' },
    { id: 'uploaded_by', label: 'Uploaded By' },
] as Array<{
    id: string;
    label: string;
    required?: boolean;
    mandatory?: boolean;
}>;

/** Column id derived from UPLOAD_HISTORY_TABLE_COLUMN_OPTIONS. */
export type UploadHistoryTableColumnId = (typeof UPLOAD_HISTORY_TABLE_COLUMN_OPTIONS)[number]['id'];

/** Per-column user preference: visibility and pin (required) state. */
export interface UploadHistoryTableColumnSetting {
    id: UploadHistoryTableColumnId;
    visible: boolean;
    required: boolean;
}

/** Totals for the claims currently in scope, from /v1/claims/summary/.
 * Each *Count is the number of claims with a non-zero value in that money
 * column, which is what a total actually covers -- claims sit at 0 until the
 * matching 835 lands, so "total paid" alone would overstate its reach. */
export interface ClaimsSummary {
    totalClaims: number;
    billedAmount: number;
    billedCount: number;
    paidAmount: number;
    paidCount: number;
    patientBalance: number;
    patientBalanceCount: number;
    appealedCount: number;
}
